// Top-level shell. The chat fills the whole surface. The header + fade
// backdrop live inside ChatView's chat column, so an open inline snippet
// keeps a clean top edge.

import { useEffect, useState } from 'react';
import { ChatView } from './components/chat/ChatView';
import { Sidebar } from './components/layout/Sidebar';
import { SidebarResizer } from './components/layout/SidebarResizer';
import { SettingsSheet } from './components/layout/SettingsSheet';
import { DragDropOverlay } from './components/ui/DragDropOverlay';
import { DEFAULT_AGENT_ID, useChatStore } from './store/chatStore';
import { useSettingsStore, type ThemeMode } from './store/settingsStore';

// Render a favicon matching the sidebar squircle: theme accent background
// with the active agent's initial. Read the CSS var on each call so it
// tracks light/dark/system + custom palettes.
function updateFavicon(initial: string) {
  const accent =
    getComputedStyle(document.documentElement).getPropertyValue('--color-accent').trim() ||
    '#7099db';
  const safe = (initial || 'N').slice(0, 1).toUpperCase();
  // Persist the initial so the pre-React inline script in index.html can
  // render the right squircle on the next reload, before JS boots.
  try {
    localStorage.setItem('niu.faviconInitial', safe);
  } catch {}
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<rect width="64" height="64" rx="16" ry="16" fill="${accent}"/>` +
    `<text x="50%" y="56%" text-anchor="middle" font-family="-apple-system,BlinkMacSystemFont,Segoe UI,Inter,sans-serif" font-size="40" font-weight="700" fill="white" dominant-baseline="middle">${safe}</text>` +
    `</svg>`;
  const href = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  // Chrome only re-fetches the favicon when the <link> element itself is
  // replaced — mutating .href in place is silently ignored. Remove every
  // existing icon link (including the static one from index.html) and
  // insert a fresh one.
  document.querySelectorAll('link[rel~="icon"]').forEach((el) => el.remove());
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/svg+xml';
  link.href = href;
  document.head.appendChild(link);
}

// everforest / espresso (+ their light variants) are full palettes applied via
// `data-theme`; light / dark / system toggle the `.dark` class on the default
// palette.
const PALETTE_THEMES = new Set<ThemeMode>([
  'everforest',
  'everforest-light',
  'espresso',
  'espresso-light',
  'lavendel',
  'lavendel-dark',
]);

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  if (PALETTE_THEMES.has(mode)) {
    root.dataset.theme = mode;
    root.classList.remove('dark');
    return;
  }
  delete root.dataset.theme;
  const isDark =
    mode === 'dark' ||
    (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  root.classList.toggle('dark', isDark);
}

export default function App() {
  const hasCredentials = useSettingsStore((s) => s.hasCredentials);
  const themeMode = useSettingsStore((s) => s.themeMode);
  const sidebarPinned = useSettingsStore((s) => s.sidebarPinned);
  const sidebarWidth = useSettingsStore((s) => s.sidebarWidth);
  const connection = useChatStore((s) => s.connection);
  const connect = useChatStore((s) => s.connect);
  const agents = useChatStore((s) => s.agents);
  const activeAgentId = useChatStore((s) => s.activeAgentId);
  const totalUnread = useChatStore((s) =>
    Object.values(s.unread).reduce((n, c) => n + (c || 0), 0),
  );

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // collapsed by default

  // Theme application + live response to OS theme changes in `system` mode.
  useEffect(() => {
    applyTheme(themeMode);
    if (themeMode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [themeMode]);

  // Document title tracks the active agent (with a leading unread counter so
  // the tab is informative when backgrounded). Favicon mirrors the sidebar
  // squircle: theme accent background + agent initial. Runs after the theme
  // effect so getComputedStyle sees the freshly-applied --color-accent.
  useEffect(() => {
    const active = agents.find((a) => a.id === activeAgentId);
    const name =
      active?.name ?? (activeAgentId === DEFAULT_AGENT_ID || agents.length === 0 ? 'Niu' : 'Messages');
    const badge = totalUnread > 0 ? `(${totalUnread > 99 ? '99+' : totalUnread}) ` : '';
    document.title = `${badge}${name}`;
    updateFavicon(name);
  }, [agents, activeAgentId, totalUnread, themeMode]);

  // Auto-connect once a token exists; force the settings modal when it doesn't.
  useEffect(() => {
    if (hasCredentials) {
      if (connection.kind === 'disconnected') connect();
    } else {
      setSettingsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCredentials]);

  // Auto-reconnect: tab refocus, browser back online, or exponential backoff
  // while visible. Each connect() derives `since_seq` from persisted history,
  // so the server replays whatever was missed during the gap.
  useEffect(() => {
    if (!hasCredentials) return;

    let retryTimer: number | null = null;
    let backoffMs = 1000;

    const needsConnect = () => {
      const k = useChatStore.getState().connection.kind;
      return k === 'disconnected' || k === 'error';
    };
    const tryNow = () => {
      if (retryTimer != null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      backoffMs = 1000;
      if (needsConnect()) useChatStore.getState().connect();
    };
    const scheduleRetry = () => {
      if (retryTimer != null || document.hidden || !needsConnect()) return;
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        if (document.hidden || !needsConnect()) return;
        useChatStore.getState().connect();
        backoffMs = Math.min(backoffMs * 2, 30_000);
        scheduleRetry();
      }, backoffMs);
    };

    const onVisibility = () => {
      // Tell the server whether this tab is in the foreground so it can
      // suppress phone pushes while the user is reading here.
      useChatStore.getState().reportPresence(document.hidden ? 'inactive' : 'active');
      if (!document.hidden) tryNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', tryNow);
    window.addEventListener('focus', tryNow);

    const unsub = useChatStore.subscribe((state, prev) => {
      if (state.connection.kind === prev.connection.kind) return;
      if (state.connection.kind === 'connected' || state.connection.kind === 'connecting') {
        backoffMs = 1000;
        if (retryTimer != null) {
          clearTimeout(retryTimer);
          retryTimer = null;
        }
      } else {
        scheduleRetry();
      }
    });

    scheduleRetry();

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', tryNow);
      window.removeEventListener('focus', tryNow);
      unsub();
      if (retryTimer != null) clearTimeout(retryTimer);
    };
  }, [hasCredentials]);

  return (
    <div className="relative flex h-full w-full overflow-hidden bg-background text-text-primary">
      {/* Pinned sidebar — permanent left column on desktop; on mobile the
          overlay drawer is still used regardless of the pinned flag. */}
      {sidebarPinned ? (
        <div className="hidden h-full shrink-0 md:flex">
          <Sidebar width={sidebarWidth} />
          <SidebarResizer />
        </div>
      ) : null}

      <div className="relative h-full min-w-0 flex-1">
        <ChatView
          onOpenSidebar={() => setSidebarOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      </div>

      {/* Overlay drawer — used when not pinned (any viewport), or when the
          user opens the menu on a mobile viewport even if pinned is on. */}
      {sidebarOpen ? (
        <div className="fixed inset-0 z-40 flex">
          <div
            className="absolute inset-0 bg-black/40 animate-fade-in"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
          <div className="relative h-full animate-fade-in">
            <Sidebar onPickAgent={() => setSidebarOpen(false)} />
          </div>
        </div>
      ) : null}

      <SettingsSheet
        open={settingsOpen}
        forced={!hasCredentials}
        onClose={() => setSettingsOpen(false)}
      />

      <DragDropOverlay />
    </div>
  );
}
